import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Typography,
  Hidden,
  BottomNavigation,
  BottomNavigationAction,
  useTheme,
} from '@material-ui/core';
import { FaHome, FaFilter, FaCalendarAlt } from 'react-icons/fa';
import { endOfYear, format, startOfYear } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import FiltersList from '../../components/utils/FiltersList';
import api from '../../services/api';
import { useAuth } from '../../hooks/auth';
import { usePreferences } from '../../hooks/preferences';
import FilterModalSimplified from '../../components/utils/FilterModalSimplified';
import YearSelector from '../../components/selectors/YearSelector';
import ResultsCard from './ResultsCard';
import MainImpactChart from './MainImpactChart';
import ResultsChart from './ResultsChart';
import {
  IEquipmentRelevance,
  IGeneralRelevance,
  IMonthlyRelevance,
  IMultiParameters,
  IRelevanceAnalisysReturn,
  ISingParameters,
} from '../../types/kpis';
import { useStyles } from './styles';
import { useWindowSize } from '../../hooks/windowSize';
import ResultsCardSkeleton from '../../components/utils/Skeletons/ResultsCardSkeleton';
import ChartSkeleton from '../../components/utils/Skeletons/ChartSkeleton';
import { calculateAverage } from '../../utils/helper';
import FilterByYear from '../../components/utils/FilterByYear';

type Metrics = 'DF' | 'UF' | 'MTBF' | 'MTTR';

interface GetChartsWidthProps {
  percentage: number;
  defaultWidth: number;
  margin?: number;
}

const Results: React.FC = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const theme = useTheme();
  const modalRef = useRef<any>();
  const { jwt } = useAuth();
  const size = useWindowSize();
  const history = useHistory();
  const { disableTabs, selectDataType } = usePreferences();
  const [currentYear, setCurrentYear] = useState<number>(
    new Date().getFullYear(),
  );
  const [activeFilters, setActiveFilters] = useState<string[]>([]);
  const [temporaryFilters, setTemporaryFilters] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [selectedParameter, setSelectedParameter] = useState<Metrics>('DF');
  const [chartLabel, setChartLabel] = useState(
    t('screens:results.impactsOnPhysicalAvailabilityResults'),
  );
  const [generalData, setGeneralData] = useState<IGeneralRelevance>(
    {} as IGeneralRelevance,
  );
  const [monthlyData, setMonthlyData] = useState<IMonthlyRelevance>(
    {} as IMonthlyRelevance,
  );
  const [equipmentsData, setEquipmentsData] = useState<IEquipmentRelevance[]>(
    [],
  );
  const [filteredEquipments, setFilteredEquipments] = useState<
    IEquipmentRelevance[]
  >([]);
  const [openFilterByYearModal, setOpenFilterByYearModal] = useState(false);

  const getRelevanceAnalisys = useCallback(
    async (yr: number): Promise<void> => {
      const startDate = format(startOfYear(new Date(yr, 1, 1)), 'yyyy-MM-dd');
      const endDate = format(endOfYear(new Date(yr, 1, 1)), 'yyyy-MM-dd');
      try {
        const res = await api.get<IRelevanceAnalisysReturn>(
          `relevance_analisys/${startDate}/${endDate}`,
          {
            headers: {
              'x-access-token': jwt,
            },
          },
        );
        setGeneralData(res.data.relevance_analisys.general);
        setMonthlyData(res.data.relevance_analisys.monthly);
        setEquipmentsData(res.data.relevance_analisys.equipments);
        // setFilteredEquipments(res.data.relevance_analisys.equipments);
        setLoading(false);
      } catch {
        // toast.error(t('screens:dimensioning.unableToLoadTotalStaff'));
      }
    },
    [jwt],
  );

  const handleFilters = (name: string, active: boolean): void => {
    const index = temporaryFilters.findIndex(ft => ft === name);
    if (index === -1 && active) {
      const newFilters = [...temporaryFilters, name];
      setTemporaryFilters(newFilters);
      return;
    }

    if (!active) {
      const newFilters = [...temporaryFilters];
      newFilters.splice(index, 1);
      setTemporaryFilters(newFilters);
    }
  };

  const handleToggleFilter = (): void => {
    if (filteredEquipments.length > 0) {
      setFilteredEquipments([]);
      modalRef.current.clearFilter();
      return;
    }
    setShowFilterModal(!showFilterModal);
  };

  const filterLabel = useMemo(() => {
    if (filteredEquipments.length > 0) {
      return t('components:buttons.clear');
    }
    return t('components:buttons.filter');
  }, [filteredEquipments, t]);

  const equipmentsAverage = useMemo(() => {
    const rawAverage_uf: number[] = [];
    const rawAverage_df: number[] = [];
    const rawAverage_mtbf: number[] = [];
    const rawAverage_mttr: number[] = [];
    const rawUF: ISingParameters[][] = [];
    const rawDF: ISingParameters[][] = [];
    const rawMTBF: ISingParameters[][] = [];
    const rawMTTR: ISingParameters[][] = [];
    const multiUF: IMultiParameters[] = [];
    const multiDF: IMultiParameters[] = [];
    const multiMTBF: IMultiParameters[] = [];
    const multiMTTR: IMultiParameters[] = [];
    const UF: ISingParameters[] = [];
    const DF: ISingParameters[] = [];
    const MTBF: ISingParameters[] = [];
    const MTTR: ISingParameters[] = [];

    filteredEquipments.forEach(ftEquip => {
      rawAverage_uf.push(ftEquip.average_uf);
      rawAverage_df.push(ftEquip.average_df);
      rawAverage_mtbf.push(ftEquip.average_mtbf);
      rawAverage_mttr.push(ftEquip.average_mttr);
      rawUF.push(ftEquip.UF);
      rawDF.push(ftEquip.DF);
      rawMTBF.push(ftEquip.MTBF);
      rawMTTR.push(ftEquip.MTTR);
    });

    const average_uf = calculateAverage(rawAverage_uf);
    const average_df = calculateAverage(rawAverage_df);
    const average_mtbf = calculateAverage(rawAverage_mtbf);
    const average_mttr = calculateAverage(rawAverage_mttr);

    for (let i = 0; i < rawUF.length; i += 1) {
      for (let j = 0; j < rawUF[i].length; j += 1) {
        const parameterIndex = multiUF.findIndex(
          par => par.name === rawUF[i][j].name,
        );
        if (parameterIndex !== -1) {
          multiUF[parameterIndex].values.push(rawUF[i][j].value);
          multiDF[parameterIndex].values.push(rawDF[i][j].value);
          multiMTBF[parameterIndex].values.push(rawMTBF[i][j].value);
          multiMTTR[parameterIndex].values.push(rawMTTR[i][j].value);
        } else {
          multiUF.push({
            name: rawUF[i][j].name,
            values: [rawUF[i][j].value],
          });
          multiDF.push({
            name: rawDF[i][j].name,
            values: [rawDF[i][j].value],
          });
          multiMTBF.push({
            name: rawMTBF[i][j].name,
            values: [rawMTBF[i][j].value],
          });
          multiMTTR.push({
            name: rawMTTR[i][j].name,
            values: [rawMTTR[i][j].value],
          });
        }
      }
    }

    for (let k = 0; k < multiUF.length; k += 1) {
      UF.push({
        name: multiUF[k].name,
        value: calculateAverage(multiUF[k].values),
      });
      DF.push({
        name: multiDF[k].name,
        value: calculateAverage(multiDF[k].values),
      });
      MTBF.push({
        name: multiMTBF[k].name,
        value: calculateAverage(multiMTBF[k].values),
      });
      MTTR.push({
        name: multiMTTR[k].name,
        value: calculateAverage(multiMTTR[k].values),
      });
    }

    return {
      shouldUseFiltered: filteredEquipments.length > 0,
      average_uf,
      average_df,
      average_mtbf,
      average_mttr,
      UF,
      DF,
      MTBF,
      MTTR,
    };
  }, [filteredEquipments]);

  const getChartsWidth = useCallback(
    ({ percentage, defaultWidth, margin }: GetChartsWidthProps): number => {
      if (size.width) {
        if (size.width < 900) {
          return size.width;
        }
        return margin
          ? size.width * percentage - margin
          : size.width * percentage;
      }
      return defaultWidth;
    },
    [size.width],
  );

  useEffect(() => {
    getRelevanceAnalisys(currentYear);
  }, [currentYear, jwt, t, getRelevanceAnalisys]);

  useEffect(() => {
    selectDataType('drills');
    disableTabs({
      roads: true,
      trucks: true,
      drills: false,
    });
  }, [selectDataType, disableTabs]);

  useEffect(() => {
    if (selectedParameter === 'DF') {
      setChartLabel(t('screens:results.impactsOnPhysicalAvailabilityResults'));
    } else if (selectedParameter === 'UF') {
      setChartLabel(t('screens:results.impactsOnPhysicalUtilizationResults'));
    } else if (selectedParameter === 'MTBF') {
      setChartLabel(t('screens:results.impactsOnMTBFResults'));
    } else if (selectedParameter === 'MTTR') {
      setChartLabel(t('screens:results.impactsOnMTTRResults'));
    }
  }, [selectedParameter, t]);

  return (
    <div className={classes.container}>
      <FiltersList
        loading={loading}
        filterLabel={filterLabel}
        type="relevance_analysis"
        data={
          filteredEquipments.length > 0 ? filteredEquipments : equipmentsData
        }
        activeFilters={activeFilters}
        temporaryFilters={temporaryFilters}
        onChange={() => handleToggleFilter()}
        onChangeFilter={(name, active) => handleFilters(name, active)}
      />
      <div className={classes.main}>
        <div className={classes.mainImpactsContainer}>
          <div className={classes.titleContainer}>
            <Typography variant="h2" className={classes.title}>
              {t('screens:results.mainImpactsTitle')}
            </Typography>
            <Hidden smDown>
              <YearSelector
                value={currentYear}
                onChange={y => setCurrentYear(y)}
              />
            </Hidden>
          </div>
          <div className={classes.mainImpactsContent}>
            <div className={classes.mainImpactsCards}>
              {loading ? (
                [1, 2, 3, 4].map(val => <ResultsCardSkeleton key={val} />)
              ) : (
                <>
                  <ResultsCard
                    title="DF (%)"
                    value={
                      equipmentsAverage.shouldUseFiltered
                        ? equipmentsAverage.average_df
                        : generalData.average_df
                    }
                    selected={selectedParameter === 'DF'}
                    onClick={() => setSelectedParameter('DF')}
                  />
                  <ResultsCard
                    title="UF (%)"
                    value={
                      equipmentsAverage.shouldUseFiltered
                        ? equipmentsAverage.average_uf
                        : generalData.average_uf
                    }
                    selected={selectedParameter === 'UF'}
                    onClick={() => setSelectedParameter('UF')}
                  />
                  <ResultsCard
                    title={`MTBF (${t(
                      'components:utils.hours',
                    ).toUpperCase()})`}
                    value={
                      equipmentsAverage.shouldUseFiltered
                        ? equipmentsAverage.average_mtbf
                        : generalData.average_mtbf
                    }
                    selected={selectedParameter === 'MTBF'}
                    onClick={() => setSelectedParameter('MTBF')}
                  />
                  <ResultsCard
                    title={`MTTR (${t(
                      'components:utils.hours',
                    ).toUpperCase()})`}
                    value={
                      equipmentsAverage.shouldUseFiltered
                        ? equipmentsAverage.average_mttr
                        : generalData.average_mttr
                    }
                    selected={selectedParameter === 'MTTR'}
                    onClick={() => setSelectedParameter('MTTR')}
                  />
                </>
              )}
            </div>
            <div className={classes.mainImpactsChart}>
              {loading ? (
                <ChartSkeleton
                  width={getChartsWidth({
                    percentage: 0.8 * 0.7,
                    defaultWidth: 600,
                    margin: 48,
                  })}
                  height={320}
                />
              ) : (
                <MainImpactChart
                  data={
                    equipmentsAverage.shouldUseFiltered
                      ? equipmentsAverage[selectedParameter]
                      : generalData[selectedParameter]
                  }
                  width={getChartsWidth({
                    percentage: 0.8 * 0.7,
                    defaultWidth: 600,
                    margin: 48,
                  })}
                />
              )}
            </div>
          </div>
          <div className={classes.resultsChart}>
            <div className={classes.titleContainer}>
              <Typography variant="h2" className={classes.title}>
                {chartLabel}
              </Typography>
            </div>
            {loading ? (
              <ChartSkeleton
                width={getChartsWidth({
                  percentage: 0.8,
                  defaultWidth: 600,
                  margin: 80,
                })}
                height={400}
              />
            ) : (
              <ResultsChart
                parameter={selectedParameter}
                data={monthlyData[selectedParameter]}
                width={getChartsWidth({
                  percentage: 0.8,
                  defaultWidth: 600,
                  margin: 80,
                })}
              />
            )}
          </div>
        </div>
      </div>
      <FilterModalSimplified
        ref={modalRef}
        equipments={equipmentsData}
        open={showFilterModal}
        onClose={() => setShowFilterModal(false)}
        onDone={() => setShowFilterModal(false)}
        filtered={equips => setFilteredEquipments(equips)}
      />

      <FilterByYear
        min={2015}
        max={new Date().getFullYear()}
        open={openFilterByYearModal}
        selectedYear={currentYear}
        onClose={() => setOpenFilterByYearModal(false)}
        onChangeYear={year => setCurrentYear(year)}
      />

      {/* Bottom tab bar, only on xs and sm screens */}
      <Hidden mdUp>
        <BottomNavigation showLabels className={classes.bottomNavigation}>
          <BottomNavigationAction
            className={classes.bottomNavLabel}
            label={t('components:nav.home')}
            onClick={() => history.push('/dashboard')}
            icon={<FaHome size={24} color={theme.palette.common.white} />}
          />
          <BottomNavigationAction
            className={classes.bottomNavLabel}
            label={
              filteredEquipments.length > 0
                ? filterLabel
                : t('screens:results.filterByDrill')
            }
            onClick={() => handleToggleFilter()}
            icon={<FaFilter size={24} color={theme.palette.common.white} />}
            style={{
              borderStyle: 'solid',
              borderLeftWidth: 1,
              borderColor: '#FFF',
            }}
          />
          <BottomNavigationAction
            className={classes.bottomNavLabel}
            label={t('screens:results.filterByYear')}
            onClick={() => setOpenFilterByYearModal(true)}
            icon={
              <FaCalendarAlt size={24} color={theme.palette.common.white} />
            }
            style={{
              borderStyle: 'solid',
              borderLeftWidth: 1,
              borderColor: '#FFF',
            }}
          />
        </BottomNavigation>
      </Hidden>
    </div>
  );
};

export default Results;
